import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Container,
  Card,
  CardHeader,
  Divider,
  CardContent,
  Hidden,
  IconButton,
  Toolbar,
  Grid,
  Button,
  Tab,
  Tabs,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImportIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { formatErrorMessage } from '../utils/common';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import { hide, show } from '../store/actions/busyIndicatorActions';
import { AxiosCompanyDashboardFleetIoConfigsClient } from '../proxy/ProxyClients';
import { CompanyDashboardFleetIoConfigReq, CompanyDashboardFleetIoConfigRes, EnumRes, FleetIoFormType } from '../proxy/proxy';
import AppConstants from '../constants/AppConstants';
import Table from '../components/Table';
import MobileDataList from '../components/MobileDataList';
import FetchFleetIoForms from '../components/fleetio/FetchFleetIoForms';
import ConfirmButton from '../components/ConfirmButton';
import EnumService from '../services/EnumService';

interface Props {
  showAlert: typeof showGlobalAlert;
  clearAllAlerts: typeof clearAllGlobalAlerts;
  showBusyIndicator: typeof show;
  hideBusyIndicator: typeof hide;
}

interface ConfigItem {
  id: number;
  name: string;
  stateIds?: number[] | null;
  fleetIoFormType: number;
}

const FleetIoConfig: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
}) => {
  const [formData, setFormData] = useState<ConfigItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [sortModel, setSortModel] = useState<any>([]);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedStateIdx, setSelectedStateIdx] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [allStates, setAllStates] = useState<EnumRes[]>([]);
  const [fleetIoFormTypes, setFleetIoFormTypes] = useState<EnumRes[]>([]);

  const loadData = useCallback(() => {
    showBusyIndicator();
    clearAllAlerts();

    EnumService.getEnums('states')
      .then((states) => {
        setAllStates(states);
      });

    EnumService.getEnums('fleetIoFormTypes')
      .then((formTypes) => {
        setFleetIoFormTypes(formTypes);
      });

    AxiosCompanyDashboardFleetIoConfigsClient.get()
      .then((response) => {
        // Use the new unified forms array with fleetIoFormType
        const forms = (response?.config?.forms || []).map((element) => ({
          id: element.id || 0,
          name: element.name || '',
          fleetIoFormType: element.fleetIoFormType || FleetIoFormType._0,
          stateIds: element.stateIds || null,
        }));
        setFormData(forms);
      })
      .catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      })
      .finally(() => {
        hideBusyIndicator();
      });
  }, [showBusyIndicator, hideBusyIndicator, clearAllAlerts, showAlert]);

  const updateConfig = useCallback(async (newFormData: ConfigItem[]) => {
    if (isUpdating) return;

    setIsUpdating(true);
    showBusyIndicator();

    try {
      // Use the new unified forms structure
      const forms = newFormData.map((f) => ({
        id: f.id,
        name: f.name,
        stateIds: f.stateIds || null,
        fleetIoFormType: f.fleetIoFormType,
      }));

      const companyDashboardFleetIoConfigReq: CompanyDashboardFleetIoConfigReq = {
        config: {
          forms
        }
      };
      await AxiosCompanyDashboardFleetIoConfigsClient.createOrUpdate(companyDashboardFleetIoConfigReq);
      await loadData();
    } catch (err) {
      // showAlert(formatErrorMessage(JSON.parse(err?.response)));
    } finally {
      setIsUpdating(false);
      hideBusyIndicator();
    }
  }, [isUpdating, showBusyIndicator, hideBusyIndicator, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    loadData();
  };

  const handleImportOpen = () => {
    setIsImportOpen(true);
  };

  const handleImportClose = async (selectedForms: any, formType: number) => {
    setIsImportOpen(false);
    if (!selectedForms || selectedForms.length === 0) {
      return;
    }
    // Add imported forms to current state and type
    const stateId = allStates[selectedStateIdx]?.id;
    const newForms = selectedForms.map((form: any) => ({
      id: form.id,
      name: form.title,
      fleetIoFormType: formType,
      stateIds: stateId ? [stateId] : null,
    }));
    // Remove any existing forms for this state and type, then add new
    const filtered = formData.filter((f) => {
      const hasState = f.stateIds && f.stateIds.includes(stateId ?? 0);
      return !(hasState && newForms.some((nf: any) => nf.fleetIoFormType === f.fleetIoFormType));
    });
    const updated = [...filtered, ...newForms];
    await updateConfig(updated);
    setFormData(updated);
  };

  const handleDeleteClick = async (id: number) => {
    const stateId = allStates[selectedStateIdx]?.id;
    // Only delete from current state
    const newData = formData.filter((item) => !(item.id === id && item.stateIds && item.stateIds.includes(stateId ?? 0)));
    await updateConfig(newData);
    setFormData(newData);
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 120,
    },
    {
      field: 'name',
      headerName: 'Title',
      flex: 1,
    },
    {
      field: 'fleetIoFormType',
      headerName: 'Form Type',
      width: 160,
      valueGetter: (params: any) => {
        const formType = fleetIoFormTypes.find((ft) => ft.id === params.row.fleetIoFormType);
        return formType?.value || '';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <ConfirmButton
            buttonIcon={<DeleteIcon />}
            isIconButton
            dialogTitle="Are you sure?"
            showCancelButton
            message="Form will be permanently removed from configuration."
            onConfirm={() => handleDeleteClick(row.id)}
            buttonColor="secondary"
          />
        );
      }
    }
  ];

  const handleStateTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedStateIdx(newValue);
    setCurrentPage(0);
  };

  const renderEachMobileCard = (row: ConfigItem) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        avatar={<SettingsIcon />}
        action={(
          <ConfirmButton
            buttonIcon={<DeleteIcon />}
            isIconButton
            dialogTitle="Are you sure?"
            showCancelButton
            message="Form will be removed from configuration."
            onConfirm={() => handleDeleteClick(row.id)}
            buttonColor="secondary"
          />
        )}
        title={row.id}
        subheader={fleetIoFormTypes.find((ft) => ft.id === row.fleetIoFormType)?.value || ''}
      />
      <Divider />
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {row.name}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>FleetIo Config Forms | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              action={(
                <IconButton onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              )}
              title="FleetIo Forms"
            />
            <Divider />
            <CardContent>
              <Tabs aria-label="state tabs" value={selectedStateIdx} onChange={handleStateTabChange}>
                {allStates.map((state, idx) => (
                  <Tab key={state.id} value={idx} label={state?.value} />
                ))}
              </Tabs>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ImportIcon />}
                  onClick={handleImportOpen}
                >
                  Import Forms
                </Button>
              </Box>
              <Hidden mdDown>
                <Table
                  rows={formData.filter((f) => f.stateIds && allStates[selectedStateIdx] && f.stateIds.includes(allStates[selectedStateIdx].id ?? 0))}
                  columns={columns}
                  totalRows={formData.filter((f) => f.stateIds && allStates[selectedStateIdx] && f.stateIds.includes(allStates[selectedStateIdx].id ?? 0)).length}
                  page={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                  sortModel={sortModel}
                  onSortModelChange={(model) => setSortModel(model)}
                />
              </Hidden>
              <Hidden mdUp>
                <MobileDataList
                  rows={formData.filter((f) => f.stateIds && allStates[selectedStateIdx] && f.stateIds.includes(allStates[selectedStateIdx].id ?? 0))}
                  renderEachRow={renderEachMobileCard}
                  totalRows={formData.filter((f) => f.stateIds && allStates[selectedStateIdx] && f.stateIds.includes(allStates[selectedStateIdx].id ?? 0)).length}
                  page={currentPage}
                  onPageChange={(page: any) => setCurrentPage(page)}
                />
              </Hidden>
            </CardContent>
          </Card>
          <FetchFleetIoForms
            open={isImportOpen}
            selectedForms={formData.filter((f) => f.stateIds && allStates[selectedStateIdx] && f.stateIds.includes(allStates[selectedStateIdx].id ?? 0)).map((f) => ({ id: f.id, title: f.name, type: f.fleetIoFormType }))}
            stateId={allStates[selectedStateIdx]?.id ?? 0}
            handleModalClose={handleImportClose}
          />
        </Container>
      </Box>
    </>
  );
};

const mapDispatchToProps = {
  showAlert: showGlobalAlert,
  clearAllAlerts: clearAllGlobalAlerts,
  showBusyIndicator: show,
  hideBusyIndicator: hide,
};

export default connect(null, mapDispatchToProps)(FleetIoConfig);
